import { useState, useEffect, useRef } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Metronome — accurate timing via Web Audio scheduler (setTimeout-based
// metronomes drift badly). We schedule clicks N seconds into the future
// using AudioContext currentTime, and a UI tick syncs to that.

const TIME_SIGS = [
  { id: "4/4", beats: 4 },
  { id: "3/4", beats: 3 },
  { id: "2/4", beats: 2 },
  { id: "6/8", beats: 6 },
  { id: "5/4", beats: 5 },
  { id: "7/8", beats: 7 },
];

export default function Metronome() {
  const [bpm, setBpm] = useState(100);
  const [sigId, setSigId] = useState("4/4");
  const [accent, setAccent] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const ctxRef = useRef(null);
  const nextTimeRef = useRef(0);
  const beatRef = useRef(0);
  const timerRef = useRef(null);
  const tapTimes = useRef([]);

  const sig = TIME_SIGS.find((s) => s.id === sigId) || TIME_SIGS[0];

  const click = (time, isAccent) => {
    const ctx = ctxRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = isAccent ? 1200 : 800;
    osc.type = "square";
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.2, time + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(time); osc.stop(time + 0.06);
  };

  const scheduler = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const lookahead = 0.1;
    while (nextTimeRef.current < ctx.currentTime + lookahead) {
      const isAccent = accent && beatRef.current === 0;
      click(nextTimeRef.current, isAccent);
      // Schedule UI update
      const beatToShow = beatRef.current;
      const fireAt = nextTimeRef.current - ctx.currentTime;
      setTimeout(() => setCurrentBeat(beatToShow), Math.max(0, fireAt * 1000));
      nextTimeRef.current += 60 / bpm;
      beatRef.current = (beatRef.current + 1) % sig.beats;
    }
    timerRef.current = setTimeout(scheduler, 25);
  };

  const start = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      ctxRef.current = ctx;
      nextTimeRef.current = ctx.currentTime + 0.05;
      beatRef.current = 0;
      setCurrentBeat(0);
      setPlaying(true);
      scheduler();
    } catch {}
  };
  const stop = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (ctxRef.current) { try { ctxRef.current.close(); } catch {} }
    ctxRef.current = null;
    setPlaying(false);
  };

  useEffect(() => () => stop(), []);

  const tap = () => {
    const now = performance.now();
    tapTimes.current.push(now);
    // Keep last 6 taps; drop any older than 2 seconds
    tapTimes.current = tapTimes.current.filter((t) => now - t < 2000);
    if (tapTimes.current.length >= 2) {
      const gaps = [];
      for (let i = 1; i < tapTimes.current.length; i++) gaps.push(tapTimes.current[i] - tapTimes.current[i - 1]);
      const avg = gaps.reduce((s, x) => s + x, 0) / gaps.length;
      const newBpm = Math.round(60000 / avg);
      if (newBpm >= 40 && newBpm <= 240) setBpm(newBpm);
    }
  };

  return (
    <ToolsShell title="Metronome">
      <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 14, padding: "20px 22px", textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 4 }}>BPM</div>
        <div style={{ fontSize: 64, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{bpm}</div>
        {/* Beat indicator */}
        <div style={{ display: "flex", gap: 6, justifyContent: "center", marginTop: 14 }}>
          {Array.from({ length: sig.beats }).map((_, i) => (
            <div key={i} style={{
              width: i === 0 && accent ? 16 : 12,
              height: i === 0 && accent ? 16 : 12,
              borderRadius: "50%",
              background: playing && i === currentBeat ? TOOLS_ACCENT : "#262839",
              transition: "background 0.08s",
            }} />
          ))}
        </div>
      </div>

      <input
        type="range"
        min="40" max="240" value={bpm}
        onChange={(e) => setBpm(parseInt(e.target.value, 10))}
        style={{ width: "100%", accentColor: TOOLS_ACCENT, marginTop: 16 }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#5B5E7A" }}>
        <span>40</span><span>120</span><span>240</span>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 12, justifyContent: "center" }}>
        <button onClick={() => setBpm(Math.max(40, bpm - 5))} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>−5</button>
        <button onClick={() => setBpm(Math.max(40, bpm - 1))} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>−1</button>
        <button onClick={tap} style={{ background: TOOLS_ACCENT, color: "#1A0604", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Tap tempo</button>
        <button onClick={() => setBpm(Math.min(240, bpm + 1))} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>+1</button>
        <button onClick={() => setBpm(Math.min(240, bpm + 5))} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>+5</button>
      </div>

      <div style={{ marginTop: 16 }}>
        <label style={{ fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6 }}>Time signature</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {TIME_SIGS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSigId(s.id)}
              style={{ flex: 1, background: sigId === s.id ? TOOLS_ACCENT : "#1C1E2E", color: sigId === s.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 4px", fontSize: 13, fontWeight: 600, cursor: "pointer", minWidth: 50 }}
            >
              {s.id}
            </button>
          ))}
        </div>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 13, color: "#C8CADF", cursor: "pointer" }}>
        <input type="checkbox" checked={accent} onChange={(e) => setAccent(e.target.checked)} style={{ accentColor: TOOLS_ACCENT }} />
        Accent the downbeat
      </label>

      <button onClick={playing ? stop : start} style={{ ...primaryBtn, width: "100%", marginTop: 18, background: playing ? "#1C1E2E" : TOOLS_ACCENT, color: playing ? TOOLS_ACCENT : "#1A0604" }}>
        {playing ? "Stop" : "Start"}
      </button>
    </ToolsShell>
  );
}
